import { Button, Icon } from "@aidkitorg/component-library";
import { IconName } from "@aidkitorg/component-library/src/Icon";
import React, { useState } from "react";
import { MetricComponentConfig } from "./MetricComponentConfig";
import { ApplicantCountComponentConfig } from "./ApplicantCountComponentConfig";
import { BarChartComponentConfig } from "./BarChartComponentConfig";
import { LineChartComponentConfig } from "./LineChartComponentConfig";
import { PieChartComponentConfig } from "./PieChartComponentConfig";
import { MapComponentConfig } from "./MapComponentConfig";
import { SummaryTableComponentConfig } from "./SummaryTableComponentConfig";
import { useLocalizedStrings } from "../../../Localization";
import { ReportComponentConfig } from "./ReportComponentConfig";

type DashboardComponent = {
  key: 'metric' | 'applicant_count' | 'bar_chart' | 'line_chart' | 'pie_chart' | 'map' | 'report' | 'summary_table',
  name: string,
  subtitle: string,
  icon: IconName,
  onClick: React.MouseEventHandler<HTMLButtonElement>
}


type DashboardComponentOptions = {
  options: Array<DashboardComponent>
}

const DashboardComponentOptions = ({ options }: DashboardComponentOptions) => {
  return (
    <>
      {options.map((component, idx) => (
        <div key={idx}>
          <Button 
            className="border !w-full !justify-start"
            variant="secondary"
            onClick={component.onClick}
          >
            <div className="flex items-center gap-x-2">
              <div>
                <Icon type="solid" name={component.icon} role="decorative"/>
              </div>
              <div className="text-left">
                <div>
                  {component.name}
                </div>
                <div className="text-gray-700 font-normal">
                  {component.subtitle}
                </div>
              </div>
            </div>
          </Button>
        </div>
      ))}
    </>
  )
}

export type CreateComponentProps = {
  setDisplayMode: (mode: 'create') => void
  onClose?: () => void
}

export const CreateDashboardComponentTab = ({ onClose, onTitleChange }: { onClose?: () => void, onTitleChange?: (title: string) => void }) => {
  const L = useLocalizedStrings()

  const [displayMode, setDisplayMode] = useState<DashboardComponent['key'] | 'create'>('create')

  const handleSetDisplayMode = (mode: DashboardComponent['key'] | 'create') => {
    setDisplayMode(mode)

    // Update modal title based on display mode
    if (onTitleChange) {
      const titleMap: Record<DashboardComponent['key'] | 'create', string> = {
        'create': L.admin_panel.dashboards.add_widget,
        'applicant_count': L.admin_panel.dashboards.add_applicant_count,
        'metric': L.admin_panel.dashboards.add_metric,
        'bar_chart': L.admin_panel.dashboards.add_bar_chart,
        'line_chart': L.admin_panel.dashboards.add_line_chart,
        'pie_chart': L.admin_panel.dashboards.add_pie_chart,
        'map': L.admin_panel.dashboards.add_map,
        'summary_table': L.admin_panel.dashboards.add_summary_table,
        'report': L.admin_panel.dashboards.add_report,
      }
      onTitleChange(titleMap[mode])
    }
  }

  const DASHBOARD_COMPONENT_OPTIONS: Array<DashboardComponent> = [
    {
      key: 'applicant_count',
      name: L.admin_panel.dashboards.applicant_count,
      subtitle: L.admin_panel.dashboards.applicant_count_subtitle,
      icon: 'UserGroupIcon',
      onClick: () => handleSetDisplayMode('applicant_count')
    },
    {
      key: 'metric',
      name: L.admin_panel.dashboards.metric,
      subtitle: L.admin_panel.dashboards.metric_subtitle,
      icon: 'CurrencyDollarIcon',
      onClick: () => handleSetDisplayMode('metric')
    },
    {
      key: 'bar_chart',
      name: L.admin_panel.dashboards.bar_chart,
      subtitle: L.admin_panel.dashboards.bar_chart_subtitle,
      icon: 'ChartBarIcon',
      onClick: () => handleSetDisplayMode('bar_chart')
    },
    {
      key: 'line_chart',
      name: L.admin_panel.dashboards.line_chart,
      subtitle: L.admin_panel.dashboards.line_chart_subtitle,
      icon: 'PresentationChartLineIcon',
      onClick: () => handleSetDisplayMode('line_chart')
    },
    {
      key: 'pie_chart',
      name: L.admin_panel.dashboards.pie_chart,
      subtitle: L.admin_panel.dashboards.pie_chart_subtitle,
      icon: 'ChartPieIcon',
      onClick: () => handleSetDisplayMode('pie_chart')
    },
    {
      key: 'map',
      name: L.admin_panel.dashboards.map,
      subtitle: L.admin_panel.dashboards.map_subtitle,
      icon: 'MapIcon',
      onClick: () => handleSetDisplayMode('map')
    },
    {
      key: 'summary_table',
      name: L.admin_panel.dashboards.summary_table,
      subtitle: L.admin_panel.dashboards.summary_table_desc,
      icon: 'ViewColumnsIcon',
      onClick: () => handleSetDisplayMode('summary_table')
    },
    {
      key: 'report',
      name: L.admin_panel.dashboards.add_report,
      subtitle: L.admin_panel.dashboards.report_subtitle,
      icon: 'TableCellsIcon',
      onClick: () => handleSetDisplayMode('report')
    }
  ]

  return (
    <div className="flex flex-col gap-y-2">
      {displayMode === 'create' && <DashboardComponentOptions options={DASHBOARD_COMPONENT_OPTIONS} />}
      {displayMode === 'applicant_count' && <ApplicantCountComponentConfig setDisplayMode={() => handleSetDisplayMode('create')} onClose={onClose} />}
      {displayMode === 'metric' && <MetricComponentConfig setDisplayMode={() => handleSetDisplayMode('create')} onClose={onClose} />}
      {displayMode === 'bar_chart' && <BarChartComponentConfig setDisplayMode={() => handleSetDisplayMode('create')} onClose={onClose} />}
      {displayMode === 'line_chart' && <LineChartComponentConfig setDisplayMode={() => handleSetDisplayMode('create')} onClose={onClose} />}
      {displayMode === 'pie_chart' && <PieChartComponentConfig setDisplayMode={() => handleSetDisplayMode('create')} onClose={onClose} />}
      {displayMode === 'map' && <MapComponentConfig setDisplayMode={() => handleSetDisplayMode('create')} onClose={onClose} />}
      {displayMode === 'summary_table' && <SummaryTableComponentConfig setDisplayMode={() => handleSetDisplayMode('create')} onClose={onClose} />}
      {displayMode === 'report' && <ReportComponentConfig setDisplayMode={() => handleSetDisplayMode('create')} onClose={onClose} />}
    </div>
  )
}